import React, { useState } from 'react'
import { useAdvancedOverviewState } from '../../api/AdvancedOverviewProvider'
import { Failure } from '../../api/failureReason'
import { useTrackCatalog } from '../../api/useTrackCatalog'
import {
  AdvancedCareTeamMember,
  AdvancedTrackDefinition,
  AdvancedTrackLead,
  AdvancedTrackSummary,
} from '../../api/types'
import { roleLabelFor } from '../careTeam/roleCategories'
import AcceptTrackDialog, { AcceptRequest } from './AcceptTrackDialog'
import ChangeLeadDialog, { LeadTarget } from './ChangeLeadDialog'
import EndTrackDialog, { EndTrackRequest } from './EndTrackDialog'
import StartTrackDialog from './StartTrackDialog'
import { defaultLeadFor, findTrack } from './trackTransitions'
import { LeadDirectory, useLeadDirectory } from './useLeadDirectory'
import { TrackStartRequest, useTrackTransitions } from './useTrackTransitions'

const NO_CARE_TEAM: AdvancedCareTeamMember[] = []

const plannedLeadNote = (track: AdvancedTrackDefinition | undefined) => {
  if (!track?.ownerRole) {
    return 'Optional. With no lead, the patient chooses their provider when booking the intake call.'
  }
  const role = roleLabelFor(track.ownerRole)

  return `Optional. Without one, the care team's ${role} leads it; if there isn't one, you're asked when it starts.`
}

export type TrackActions = {
  catalog: AdvancedTrackDefinition[]
  leads: LeadDirectory
  busy: boolean
  // A `lead` the plan already named skips the owner-role default.
  start: (request: TrackStartRequest, onStarted?: () => void) => void
  openEnd: (request: EndTrackRequest) => void
  openLead: (track: AdvancedTrackSummary) => void
  openPlannedLead: (trackKey: string, lead: AdvancedTrackLead, onSave: (lead: AdvancedTrackLead) => void) => void
  openAccept: (request: AcceptRequest) => void
  accept: (enrollmentId: string, startsOn: string | null, onAccepted?: () => void) => void
  cancelIntake: (track: AdvancedTrackSummary) => void
  // Marks the plan bar a coach acts from, so its progress and any failure show on that bar.
  actOn: (barId: string) => void
  busyOn: (barId: string) => boolean
  failureFor: (barId: string) => Failure | null
  dismissFailure: () => void
  // Render once, anywhere in the caller's tree.
  dialog: React.ReactNode
}

export const useTrackActions = (uid: string): TrackActions => {
  const catalog = useTrackCatalog()
  const overview = useAdvancedOverviewState()
  const careTeam = overview.status === 'ready' ? overview.overview.careTeam : NO_CARE_TEAM
  const leads = useLeadDirectory(careTeam)
  const transitions = useTrackTransitions(uid)
  const [request, setRequest] = useState<EndTrackRequest | null>(null)
  const [leadTarget, setLeadTarget] = useState<(LeadTarget & { save: (lead: AdvancedTrackLead) => void }) | null>(null)
  const [startTrack, setStartTrack] = useState<{ track: AdvancedTrackDefinition; onStarted?: () => void } | null>(null)
  const [acceptRequest, setAcceptRequest] = useState<AcceptRequest | null>(null)
  // Keyed by patient too: the tab outlives a switch, and psychiatric supervision's bar id is the same on every plan.
  const [actedOn, setActedOn] = useState<{ uid: string; barId: string } | null>(null)
  const actedOnHere = (barId: string) => actedOn?.uid === uid && actedOn.barId === barId

  // With no lead named, starts at once when the owner role has someone on the care team; otherwise asks.
  const start = ({ trackKey, lead, acceptNow }: TrackStartRequest, onStarted?: () => void) => {
    const track = findTrack(catalog, trackKey)

    if (!acceptNow && lead === undefined && track?.ownerRole && !defaultLeadFor(track, careTeam)) {
      setStartTrack({ track, onStarted })
      return
    }
    transitions.start({ trackKey, lead, acceptNow }, onStarted)
  }

  const dialog = (
    <>
      <EndTrackDialog
        request={request}
        catalog={catalog}
        careTeam={careTeam}
        onCancel={() => setRequest(null)}
        onConfirm={(track, endReason, next, nextLead) => {
          setRequest(null)
          transitions.end(track.enrollmentId, { endReason, next, nextLead }, request?.onEnded)
        }}
      />
      <StartTrackDialog
        track={startTrack?.track ?? null}
        careTeam={careTeam}
        onCancel={() => setStartTrack(null)}
        onConfirm={(track, lead) => {
          setStartTrack(null)
          transitions.start({ trackKey: track.trackKey, lead }, startTrack?.onStarted)
        }}
      />
      <AcceptTrackDialog
        request={
          acceptRequest && {
            ...acceptRequest,
            confirm: (startsOn) => {
              setAcceptRequest(null)
              acceptRequest.confirm(startsOn)
            },
          }
        }
        onCancel={() => setAcceptRequest(null)}
      />
      <ChangeLeadDialog
        target={leadTarget}
        catalog={catalog}
        careTeam={careTeam}
        onCancel={() => setLeadTarget(null)}
        onConfirm={(lead) => {
          leadTarget?.save(lead)
          setLeadTarget(null)
        }}
      />
    </>
  )

  return {
    catalog,
    leads,
    busy: transitions.busy,
    // A plan edit stays clickable mid-request; it must not take the request's bar away from it.
    actOn: (barId) => {
      if (transitions.busy) {
        return
      }
      setActedOn({ uid, barId })
      transitions.dismissFailure()
    },
    busyOn: (barId) => transitions.busy && actedOnHere(barId),
    failureFor: (barId) => (actedOnHere(barId) ? transitions.failure : null),
    dismissFailure: transitions.dismissFailure,
    start,
    openEnd: setRequest,
    openLead: (track) =>
      setLeadTarget({
        trackKey: track.trackKey,
        lead: track.providerUid ? { providerUid: track.providerUid, joinAs: null } : null,
        save: (lead) => transitions.changeLead(track.enrollmentId, lead),
      }),
    openAccept: setAcceptRequest,
    accept: transitions.accept,
    cancelIntake: (track) => transitions.end(track.enrollmentId, { endReason: 'discontinued', next: null }),
    openPlannedLead: (trackKey, lead, save) =>
      setLeadTarget({
        trackKey,
        lead,
        note: plannedLeadNote(findTrack(catalog, trackKey)),
        save,
      }),
    dialog,
  }
}
